import { useNavigate, useLocation, useLoaderData, useRevalidator } from 'react-router';
import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import Page from 'client/components/aria/Page/Page';
import SortButton from 'client/components/aria/SortButton/SortButton';
import Table from 'client/components/aria/Table';
import { Cell, Column, Item, Row, TableBody, TableHeader } from 'react-stately';
import Menu from 'client/components/aria/Menu/Menu';
import Pagination from 'client/components/aria/Pagination/Pagination';
import ShowResults from 'client/components/aria/ShowResults/ShowResults';
import moment from 'moment';
import Icon from '@mdi/react';
import { mdiDotsVertical } from '@mdi/js';
import { MAP_PAGE, VIEW_ENQUIRY_PAGE } from 'client/utils/routes/routeConstants';
import styles from './ListEnquiryPage.module.scss';
import { EnquiryRequestService } from 'client/services';
import NavLink from 'client/components/aria/NavLink/NavLink';
import Alert from 'client/components/aria/Alert/Alert';
import EnquiryAlertModal from 'client/pages/EnquiriesPage/components/EnquiryAlerts/EnquiryAlertModal/EnquiryAlertModal';
import { permissions } from 'client/utils/constants/permissions';
import { useHasUserPermission } from 'client/utils/auth/userPermissions';

const MENU_ACTION = {
    VIEW_ENQUIRY: 'VIEW_ENQUIRY',
    VIEW_ON_MAP: 'VIEW_ON_MAP',
    DELETE: 'DELETE'
};

const ListEnquiryPage = () => {
    const intl = useIntl();
    const loaderData = useLoaderData();
    const { content, totalPages } = loaderData.enquiries;
    const navigate = useNavigate();
    const location = useLocation();
    const { revalidate } = useRevalidator();
    const params = new URLSearchParams(location.search);
    const currentPage = parseInt(params.get('page')) + 1 || 1;
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [enquiryToDelete, setEnquiryToDelete] = useState(null);
    const [deleteSuccessMessage, setDeleteSuccessMessage] = useState('');
    const hasDeletePermission = useHasUserPermission(permissions.ENQUIRY_DELETE);

    useEffect(() => {
        let timeoutId;
        if (deleteSuccessMessage) {
            timeoutId = setTimeout(() => {
                setDeleteSuccessMessage('');
            }, 5000);
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [deleteSuccessMessage]);

    const setDialogVisibility = (open) => {
        setShowDeleteDialog(open);
        if (!open) {
            setEnquiryToDelete(null);
        }
    };

    const onTableMenuAction = async (evt, enquiry) => {
        if (evt === MENU_ACTION.VIEW_ON_MAP) {
            navigate(MAP_PAGE + '?enquiry=' + enquiry.enquiryId);
            return;
        }
        if (evt === MENU_ACTION.VIEW_ENQUIRY) {
            navigate(VIEW_ENQUIRY_PAGE + '/' + enquiry.enquiryId);
            return;
        }
        if (evt === MENU_ACTION.DELETE) {
            setEnquiryToDelete(enquiry);
            setDialogVisibility(true);
        }
    };

    const handleConfirmDelete = async () => {
        if (!enquiryToDelete) {
            setDialogVisibility(false);
            return;
        }

        try {
            await EnquiryRequestService.deleteEnquiries([enquiryToDelete.enquiryId]);
            setDeleteSuccessMessage(intl.formatMessage(
                { id: 'listEnquiryPage.delete.success' },
                { title: enquiryToDelete.title ?? '' }
            ));
            setDialogVisibility(false);
            await revalidate();
        } catch (error) {
            setDialogVisibility(false);
        }
    };

    const onPaginationChange = page => {
        const params = new URLSearchParams(location.search);
        params.set('page', page - 1);
        navigate(location.pathname + '?' + params.toString());
    };

    return (
        <Page
            title={intl.formatMessage({ id: 'listEnquiryPage.page.title' })}
            role="main"
            data-testid="listEnquiryPage"
            className={styles.page}
        >
            <div className="govuk-grid-row">
                <div className="govuk-grid-column-full">
                    {deleteSuccessMessage && (
                        <Alert
                            variant="warning"
                            title={intl.formatMessage({ id: 'listEnquiryPage.delete.success.title' })}
                        >
                            <p className="govuk-body">{deleteSuccessMessage}</p>
                        </Alert>
                    )}
                    <h1 data-testid="page.title.enquiries" className={['govuk-heading-xl', styles.header].join(' ')}>{intl.formatMessage({ id: 'listEnquiryPage.page.title' })}</h1>
                    <hr className="govuk-section-break govuk-section-break--l govuk-section-break--visible"/>
                </div>
            </div>
            <div className={'govuk-grid-row'}>
                <div className={['govuk-grid-column-full', styles.table].join(' ')}>
                    <Table
                        aria-label={intl.formatMessage({ id: 'listEnquiryPage.page.title' })}
                        data-testid="listEnquiryPage"
                    >
                        <TableHeader>
                            <Column>
                                <SortButton column={'title'}>
                                    {intl.formatMessage({ id: 'name' })}
                                </SortButton>
                            </Column>
                            <Column>
                                <SortButton column={'dateCreated'}>
                                    {intl.formatMessage({ id: 'dateCreated' })}
                                </SortButton>
                            </Column>
                            <Column>
                                <SortButton column={'emergency'} defaultSortOrder='desc'>
                                    {intl.formatMessage({ id: 'type' })}
                                </SortButton>
                            </Column>
                            <Column>
                                <SortButton column={'startDate'}>
                                    {intl.formatMessage({ id: 'startDate' })}
                                </SortButton>
                            </Column>
                            <Column>
                                <SortButton column={'endDate'}>
                                    {intl.formatMessage({ id: 'endDate' })}
                                </SortButton>
                            </Column>
                            <Column>
                                {intl.formatMessage({ id: 'listEnquiryPage.action' })}
                            </Column>
                        </TableHeader>
                        <TableBody>
                            {content.map(enquiry => {
                                return <Row key={enquiry.enquiryId}>
                                    <Cell wrapText>
                                        <NavLink data-testid='listEnquiryPage.link' to={VIEW_ENQUIRY_PAGE + '/' + enquiry.enquiryId}>
                                            {enquiry.title}
                                        </NavLink>
                                    </Cell>
                                    <Cell>{enquiry.dateCreated ? moment(enquiry.dateCreated).format('D MMM YYYY') : ''}</Cell>
                                    <Cell>
                                        {enquiry.isEmergency
                                            ? intl.formatMessage({ id: 'listEnquiryPage.emergency' })
                                            : intl.formatMessage({ id: 'listEnquiryPage.planned' })
                                        }
                                    </Cell>
                                    <Cell>{enquiry.startDate ? moment(enquiry.startDate).format('D MMM YYYY') : ''}</Cell>
                                    <Cell>{enquiry.endDate ? moment(enquiry.endDate).format('D MMM YYYY') : ''}</Cell>
                                    <Cell horzAlign="middle">
                                        <Menu
                                            aria-label={`${enquiry.title} ${intl.formatMessage({ id: 'menu' })}`}
                                            icon={<Icon path={mdiDotsVertical} size={1}/>}
                                            onAction={evt => onTableMenuAction(evt, enquiry)}
                                            placement="bottom right"
                                        >
                                            <Item
                                                key={MENU_ACTION.VIEW_ENQUIRY}
                                                aria-label={intl.formatMessage({ id: 'listEnquiryPage.viewEnquiry' })}
                                            >
                                                {intl.formatMessage({ id: 'listEnquiryPage.viewEnquiry' })}
                                            </Item>
                                            <Item
                                                key={MENU_ACTION.VIEW_ON_MAP}
                                                aria-label={intl.formatMessage({ id: 'listEnquiryPage.viewOnMap' })}
                                            >
                                                {intl.formatMessage({ id: 'listEnquiryPage.viewOnMap' })}
                                            </Item>
                                            {hasDeletePermission && (
                                                <Item
                                                    key={MENU_ACTION.DELETE}
                                                    aria-label={intl.formatMessage({ id: 'listEnquiryPage.delete' })}
                                                    variant="destructive"
                                                    className="deleteMenuItem"
                                                >
                                                    {intl.formatMessage({ id: 'listEnquiryPage.delete' })}
                                                </Item>
                                            )}
                                        </Menu>
                                    </Cell>
                                </Row>;
                            })}
                        </TableBody>
                    </Table>
                </div>
            </div>
            <div className="govuk-grid-row">
                <div className={['govuk-grid-column-full', styles.pagination].join(' ')}>
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onChange={onPaginationChange}
                        data-testid="pagination"
                    />
                </div>
                <div className="govuk-grid-column-full govuk-!-text-align-centre">
                    <ShowResults
                        totalElements={loaderData.enquiries.totalElements}
                        size={loaderData.enquiries.size}
                    />
                </div>
            </div>
            <EnquiryAlertModal
                showWarningDialog={showDeleteDialog}
                setShowWarningDialog={setDialogVisibility}
                onConfirm={handleConfirmDelete}
                enquiryTitle={enquiryToDelete?.title ?? ''}
                variant="primary"
            />
        </Page>
    );
};

export default ListEnquiryPage;
